import './Library.css';
import html from './Library.html';
import { getArtists, getLibraries, getLibraryItems } from '../../api/embyApi';
import { createParty } from '../../api/party';
import { navigate } from '../../router';
import type { CleanupFn, RouteParams } from '../../router';
import { renderGroupedTilesInChunks, wireAlphaRail, wireSectionSearchSync } from '../../alphaIndex';
import { wireSearchFilter, wireTileButtons } from '../../tiles';
import { LOADING_HINT } from '../../loading';

/**
 * Generic "one library" page, reached from the sidebar. What it renders
 * depends entirely on the library's Emby CollectionType:
 *   - movies/tvshows/musicvideos (or anything else playable): a tile grid,
 *     same as the home page's carousels (Series/MusicArtist/MusicAlbum
 *     tiles drill further in instead of starting a party directly - see
 *     tiles.ts's DRILL_DOWN table).
 *   - music: a tile grid too, just of artists (Emby's dedicated /Artists
 *     endpoint, not a generic library listing - see
 *     GET /api/catalog/libraries/:id/artists) - Album -> Tracks is one
 *     level further in (views/artist, then views/album).
 *
 * Synchronous wrapper around an async IIFE, not `async function`
 * directly - router.ts's cleanup contract needs a CleanupFn back
 * *synchronously*, which an async function returning a Promise can't
 * do (see router.ts's own comment, and views/player/Player.ts's
 * renderPartyPlayer for the same shape). `cancelled` guards every
 * resume-after-await point so a still-in-flight fetch from a library
 * the user already navigated away from can't clobber whatever route
 * rendered after it.
 */
export function renderLibrary(root: HTMLElement, params: RouteParams): CleanupFn {
  root.innerHTML = LOADING_HINT;
  let cancelled = false;
  let cleanup: CleanupFn = () => {};

  (async () => {
    let libraries;
    try {
      libraries = await getLibraries();
    } catch {
      if (!cancelled) root.innerHTML = '<p class="error">Impossible de charger les bibliothèques.</p>';
      return;
    }
    if (cancelled) return;

    const library = libraries.find((l) => l.id === params.id);
    if (!library) {
      root.innerHTML = '<p class="error">Bibliothèque introuvable.</p>';
      return;
    }

    root.innerHTML = html;
    root.querySelector('h1')!.textContent = library.name;

    const grid = root.querySelector('#grid') as HTMLElement;
    let items;
    try {
      items = library.collectionType === 'music'
        ? await getArtists(library.id)
        : await getLibraryItems(library.id);
    } catch {
      if (!cancelled) {
        grid.innerHTML = '<p class="error">Impossible de charger cette bibliothèque.</p>';
        root.querySelector('#searchInput')!.remove();
      }
      return;
    }
    if (cancelled) return;
    if (!items.length) {
      grid.innerHTML = '<p class="hint">Bibliothèque vide.</p>';
      root.querySelector('#searchInput')!.remove();
      return;
    }

    renderGroupedTilesInChunks(grid, items);
    wireTileButtons(grid, async (itemId) => {
      const party = await createParty(itemId);
      navigate(`/party/${party.partyId}`);
    });
    const searchInput = root.querySelector('#searchInput') as HTMLInputElement;
    wireSearchFilter(searchInput, root, '.tile');
    wireSectionSearchSync(searchInput, grid);
    cleanup = wireAlphaRail(root, grid, items);
  })();

  return () => {
    cancelled = true;
    cleanup();
  };
}
